import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Avatar,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import {
  Work,
  People,
  TrendingUp,
  Schedule,
  Refresh,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Business as BusinessIcon,
  Description,
  PersonAdd,
  Assessment
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { useEntity } from '../../context/EntityContext';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface Entity {
  id: string;
  name: string;
  subdomain: string;
  is_active: boolean;
}

interface EntityStats {
  // Users
  totalUsers: number;
  adminUsers: number;
  hrUsers: number;
  recruiterUsers: number;

  // Jobs
  totalJobs: number;
  activeJobs: number;
  pausedJobs: number;
  closedJobs: number;

  // Applications
  totalApplications: number;
  newApplications: number;
  pendingApplications: number;
  reviewingApplications: number;
  shortlistedApplications: number;
  rejectedApplications: number;
  hiredApplications: number;

  // Metrics
  conversionRate: number;
  avgDaysToHire: number;
}

interface ApplicationTrend {
  date: string;
  applications: number;
  hired: number;
}

interface JobPerformance {
  id: string;
  title: string;
  applicationsCount: number;
  hiredCount: number;
  conversionRate: number;
}

const initialStats: EntityStats = {
  totalUsers: 0,
  adminUsers: 0,
  hrUsers: 0,
  recruiterUsers: 0,
  totalJobs: 0,
  activeJobs: 0,
  pausedJobs: 0,
  closedJobs: 0,
  totalApplications: 0,
  newApplications: 0,
  pendingApplications: 0,
  reviewingApplications: 0,
  shortlistedApplications: 0,
  rejectedApplications: 0,
  hiredApplications: 0,
  conversionRate: 0,
  avgDaysToHire: 0
};

export const EntityAnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading: contextLoading } = useEntity();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [stats, setStats] = useState<EntityStats>(initialStats);
  const [applicationTrends, setApplicationTrends] = useState<ApplicationTrend[]>([]);
  const [jobPerformance, setJobPerformance] = useState<JobPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load entities on mount
  useEffect(() => {
    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check super admin access
  useEffect(() => {
    if (!contextLoading && !isSuperAdmin) {
      navigate('/admin/dashboard');
    }
  }, [isSuperAdmin, contextLoading, navigate]);

  // Load stats when entity changes
  useEffect(() => {
    if (selectedEntityId) {
      const entity = entities.find(e => e.id === selectedEntityId);
      setSelectedEntity(entity || null);
      loadEntityStats(selectedEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityId, entities]);

  const loadEntities = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('entities')
        .select('id, name, subdomain, is_active')
        .order('name');

      if (fetchError) throw fetchError;
      setEntities(data || []);

      // Auto-select first entity
      if (data && data.length > 0 && !selectedEntityId) {
        setSelectedEntityId(data[0].id);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
      setLoading(false);
    }
  };

  const loadEntityStats = useCallback(async (entityId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users stats
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId);

      const { count: adminUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('role', 'admin');

      const { count: hrUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('role', 'hr');

      const { count: recruiterUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('role', 'recruiter');

      // Fetch jobs stats
      const { count: totalJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId);

      const { count: activeJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'active');

      const { count: pausedJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'paused');

      const { count: closedJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'closed');

      // Fetch applications stats
      const { count: totalApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId);

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { count: newApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .gte('submitted_at', sevenDaysAgo.toISOString());

      const { count: pendingApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'pending');

      const { count: reviewingApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'reviewing');

      const { count: shortlistedApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'shortlisted');

      const { count: rejectedApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'rejected');

      const { count: hiredApplications } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'hired');

      // Calculate conversion rate
      const conversionRate = totalApplications && totalApplications > 0
        ? Math.round((hiredApplications || 0) / totalApplications * 100)
        : 0;

      // Calculate avg days to hire
      const { data: hiredApps } = await supabase
        .from('applications')
        .select('submitted_at, updated_at')
        .eq('entity_id', entityId)
        .eq('status', 'hired')
        .not('updated_at', 'is', null);

      let avgDaysToHire = 0;
      if (hiredApps && hiredApps.length > 0) {
        const totalDays = hiredApps.reduce((acc, app) => {
          const submitted = new Date(app.submitted_at);
          const updated = new Date(app.updated_at);
          const days = Math.ceil((updated.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
          return acc + days;
        }, 0);
        avgDaysToHire = Math.round(totalDays / hiredApps.length);
      }

      setStats({
        totalUsers: totalUsers || 0,
        adminUsers: adminUsers || 0,
        hrUsers: hrUsers || 0,
        recruiterUsers: recruiterUsers || 0,
        totalJobs: totalJobs || 0,
        activeJobs: activeJobs || 0,
        pausedJobs: pausedJobs || 0,
        closedJobs: closedJobs || 0,
        totalApplications: totalApplications || 0,
        newApplications: newApplications || 0,
        pendingApplications: pendingApplications || 0,
        reviewingApplications: reviewingApplications || 0,
        shortlistedApplications: shortlistedApplications || 0,
        rejectedApplications: rejectedApplications || 0,
        hiredApplications: hiredApplications || 0,
        conversionRate,
        avgDaysToHire
      });

      // Load application trends (last 30 days)
      await loadApplicationTrends(entityId);

      // Load job performance
      await loadJobPerformance(entityId);

    } catch (err: any) {
      console.error('Error loading entity stats:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApplicationTrends = async (entityId: string) => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data } = await supabase
        .from('applications')
        .select('submitted_at, status')
        .eq('entity_id', entityId)
        .gte('submitted_at', thirtyDaysAgo.toISOString());

      // Group by date
      const grouped: { [key: string]: { applications: number; hired: number } } = {};

      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        grouped[dateStr] = { applications: 0, hired: 0 };
      }

      (data || []).forEach(app => {
        const dateStr = app.submitted_at.split('T')[0];
        if (grouped[dateStr]) {
          grouped[dateStr].applications++;
          if (app.status === 'hired') {
            grouped[dateStr].hired++;
          }
        }
      });

      const trends = Object.entries(grouped)
        .map(([date, values]) => ({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          ...values
        }))
        .reverse();

      setApplicationTrends(trends);
    } catch (err) {
      console.error('Error loading trends:', err);
    }
  };

  const loadJobPerformance = async (entityId: string) => {
    try {
      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, title')
        .eq('entity_id', entityId)
        .eq('status', 'active')
        .limit(10);

      if (!jobs) return;

      const performance: JobPerformance[] = [];

      for (const job of jobs) {
        const { count: appCount } = await supabase
          .from('applications')
          .select('*', { count: 'exact', head: true })
          .eq('job_id', job.id);

        const { count: hiredCount } = await supabase
          .from('applications')
          .select('*', { count: 'exact', head: true })
          .eq('job_id', job.id)
          .eq('status', 'hired');

        performance.push({
          id: job.id,
          title: job.title,
          applicationsCount: appCount || 0,
          hiredCount: hiredCount || 0,
          conversionRate: appCount && appCount > 0
            ? Math.round((hiredCount || 0) / appCount * 100)
            : 0
        });
      }

      setJobPerformance(performance.sort((a, b) => b.applicationsCount - a.applicationsCount));
    } catch (err) {
      console.error('Error loading job performance:', err);
    }
  };

  const statusPieData = [
    { name: 'Pending', value: stats.pendingApplications, color: '#FFA726' },
    { name: 'Reviewing', value: stats.reviewingApplications, color: '#42A5F5' },
    { name: 'Shortlisted', value: stats.shortlistedApplications, color: '#7E57C2' },
    { name: 'Rejected', value: stats.rejectedApplications, color: '#EF5350' },
    { name: 'Hired', value: stats.hiredApplications, color: '#66BB6A' }
  ].filter(d => d.value > 0);

  if (contextLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Entity Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View detailed analytics for any entity
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Select Entity</InputLabel>
            <Select
              value={selectedEntityId}
              label="Select Entity"
              onChange={(e) => setSelectedEntityId(e.target.value)}
            >
              {entities.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => selectedEntityId && loadEntityStats(selectedEntityId)}
            disabled={loading || !selectedEntityId}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Entity Info Card */}
      {selectedEntity && (
        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                <BusinessIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {selectedEntity.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Subdomain: {selectedEntity.subdomain}
                </Typography>
              </Box>
              <Chip
                label={selectedEntity.is_active ? 'Active' : 'Inactive'}
                color={selectedEntity.is_active ? 'success' : 'default'}
                sx={{ ml: 'auto' }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {loading && selectedEntityId ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : selectedEntityId ? (
        <>
          {/* Stats Cards - Row 1: Users & Jobs */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Users */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <People sx={{ color: theme.palette.primary.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {stats.totalUsers}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Users</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Total Jobs */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                    <Work sx={{ color: theme.palette.info.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="info.main">
                      {stats.totalJobs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Jobs</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Active Jobs */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <Work sx={{ color: theme.palette.success.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {stats.activeJobs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Active Jobs</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Total Applications */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                    <Description sx={{ color: theme.palette.warning.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="warning.main">
                      {stats.totalApplications}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Applications</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Stats Cards - Row 2: Metrics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* New Applications */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                    <PersonAdd sx={{ color: theme.palette.secondary.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="secondary.main">
                      {stats.newApplications}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">New (7 days)</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Hired */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {stats.hiredApplications}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Hired</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Conversion Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <TrendingUp sx={{ color: theme.palette.success.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {stats.conversionRate}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Avg Days to Hire */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                    <Schedule sx={{ color: theme.palette.info.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="info.main">
                      {stats.avgDaysToHire}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Avg Days to Hire</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Application Status Breakdown */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Application Status Breakdown
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${alpha('#FFA726', 0.3)}`, borderRadius: 2 }}>
                <HourglassEmpty sx={{ color: '#FFA726', fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight={700}>{stats.pendingApplications}</Typography>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${alpha('#42A5F5', 0.3)}`, borderRadius: 2 }}>
                <Assessment sx={{ color: '#42A5F5', fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight={700}>{stats.reviewingApplications}</Typography>
                <Typography variant="caption" color="text.secondary">Reviewing</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${alpha('#7E57C2', 0.3)}`, borderRadius: 2 }}>
                <People sx={{ color: '#7E57C2', fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight={700}>{stats.shortlistedApplications}</Typography>
                <Typography variant="caption" color="text.secondary">Shortlisted</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${alpha('#EF5350', 0.3)}`, borderRadius: 2 }}>
                <Cancel sx={{ color: '#EF5350', fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight={700}>{stats.rejectedApplications}</Typography>
                <Typography variant="caption" color="text.secondary">Rejected</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${alpha('#66BB6A', 0.3)}`, borderRadius: 2 }}>
                <CheckCircle sx={{ color: '#66BB6A', fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight={700}>{stats.hiredApplications}</Typography>
                <Typography variant="caption" color="text.secondary">Hired</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Pie Chart */}
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Application Distribution
                  </Typography>
                  {statusPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={statusPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props: any) => `${props.name} ${(props.percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography color="text.secondary">No applications yet</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Line Chart */}
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Application Trends (Last 30 Days)
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={applicationTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="applications" stroke={theme.palette.primary.main} name="Applications" />
                      <Line type="monotone" dataKey="hired" stroke={theme.palette.success.main} name="Hired" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Job Performance Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Active Job Performance
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Applications</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Hired</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobPerformance.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No active jobs</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      jobPerformance.map((job) => (
                        <TableRow key={job.id} hover>
                          <TableCell>{job.title}</TableCell>
                          <TableCell align="right">{job.applicationsCount}</TableCell>
                          <TableCell align="right">{job.hiredCount}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${job.conversionRate}%`}
                              size="small"
                              color={job.conversionRate >= 10 ? 'success' : job.conversionRate >= 5 ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Select an entity to view analytics
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
